// #region imports
import { call, put, select } from 'redux-saga/effects';
import {
  CellPosition,
  SelectionCell,
  Sheet,
  SheetGroup,
  SheetView,
  Viewport,
} from '../../../types';
import {
  adjustNonFrozenViewportWithFreezeAc,
  calculateSheetViewport,
  loadSheet,
  loadSheetGroup,
} from './viewsActions';
import {
  addSheet,
  addSheetsGroup,
  updateSheetView,
} from '../../sheetsManagerSlice';
import { RootState } from '../../../../core/redux/store';
import {
  calcualteFirstNonFrozenIndex,
  createSheetDefaultView,
} from '../../../utils/views/views';
import {
  fitColInViewport,
  fitRowInViewport,
  getViewport,
} from '../../../utils/views/viewport';
import { Direction } from '../../../../windows/helpers/adjustWindow';
import {
  SheetAndView,
  selectSheet,
  selectSheetAndView,
  selectSheetView,
} from '../../../selectors/sheetSelector';
import { getSheetActiveCell } from '../../../databaseDeprecated/sheets/ls/ls';
import {
  convertPositionToArea,
  getDefaultPosition,
} from '../../../utils/area/area';
import { getNextVisibleRowCol } from '../../../utils/views/selection';
import { unionAreaWithMerges } from '../../../utils/views/merges';
import { SheetManagementSet } from '../../../types/sheet/draft';
import { generateSheetsDataSet } from '../root/rootHelpers';
import { startReadSheet, startReadSheetGroup } from '../../../storage/managers/ReadManager';
import { AppWindow } from '../../../../windows/types';
import { findWindow } from '../../../../windows/utils';
// #endregion

// #region adjust non frozen viewport with freeze
export function* adjustNonFrozenViewportWithFreezeMi({
  payload,
}: ReturnType<typeof adjustNonFrozenViewportWithFreezeAc>) {
  const { sheetId, adjustCols, adjustRows, windowHeight, windowWidth } =
    payload;

  const { sheet, sheetView }: SheetAndView = yield select((state: RootState) =>
    selectSheetAndView(state, sheetId)
  );

  const viewViewport = {
    colViewport: sheetView.colViewport,
    rowViewport: sheetView.rowViewport,
    filterResults: sheetView.filterResults,
  };

  let updatedColViewport: Viewport | undefined;
  let updatedRowViewport: Viewport | undefined;

  if (adjustCols && sheet.freezedCols.frozen) {
    const unFreezedCi = calcualteFirstNonFrozenIndex(
      sheet.freezedCols,
      sheet.colSizes,
      sheet.hiddenCols,
      sheet.maxColIndex,
      sheetView.colViewport.frozenIndexes
    );
    if (unFreezedCi) {
      updatedColViewport = fitColInViewport(
        unFreezedCi,
        viewViewport,
        sheet,
        windowWidth
      );
    }
  }

  if (adjustRows && sheet.freezedRows.frozen) {
    const unFreezedRi = calcualteFirstNonFrozenIndex(
      sheet.freezedRows,
      sheet.rowSizes,
      sheet.hiddenRows,
      sheet.maxRowIndex,
      sheetView.rowViewport.frozenIndexes,
      sheetView.filterResults
    );
    if (unFreezedRi) {
      const updatedViewport = fitRowInViewport(
        unFreezedRi,
        viewViewport,
        sheet,
        windowWidth,
        windowHeight
      );
      if (updatedViewport.colViewport)
        updatedColViewport = updatedViewport.colViewport;
      if (updatedViewport.rowViewport)
        updatedRowViewport = updatedViewport.rowViewport;
    }
  }

  if (updatedColViewport || updatedRowViewport) {
    yield put(
      updateSheetView({
        sheetId,
        colViewport: updatedColViewport,
        rowViewport: updatedRowViewport,
      })
    );
  }
}
// #endregion

// #region calculate sheet viewport
export function* calculateSheetViewportMi({
  payload,
}: ReturnType<typeof calculateSheetViewport>) {
  const { sheetId, width, height } = payload;
  const sheet: Sheet = yield select(
    (state: RootState) => payload.sheet ?? selectSheet(state, sheetId)
  );

  if (!sheet) return;

  let oldSheetView: SheetView | undefined = yield select((state: RootState) =>
    selectSheetView(state, sheetId)
  );
  // let sheetView = _.cloneDeep(oldSheetView);
  let sheetView = oldSheetView;
  let sheetViewExists = !!sheetView;
  let activeCell: CellPosition | undefined;
  if (!sheetViewExists) {
    const cellPos = getSheetActiveCell(sheetId) ?? getDefaultPosition();

    activeCell = getNextVisibleRowCol(
      sheet,
      cellPos.ci,
      cellPos.ri,
      sheetView?.filterResults,
      0
    );

    const activeCellArea = unionAreaWithMerges(
      sheet.merges,
      convertPositionToArea(activeCell)
    );
    const selection = {
      cell: SelectionCell.Cell,
      area: activeCellArea,
    };
    sheetView = createSheetDefaultView({
      activeCell,
      activeCellArea,
      selections: [selection],
    });

    // const newViewports = fitCellInViewport(
    //   sheet,
    //   activeCell,
    //   sheetView,
    //   width,
    //   height
    // );

    // const newSheetView = {
    //   ...sheetView,
    // colViewport: newViewports.colViewport ?? sheetView.colViewport,
    // rowViewport: newViewports.rowViewport ?? sheetView.rowViewport,
    // };
    // sheetView = newSheetView;
  }

  if (sheet && sheetView) {
    const allViewport = getViewport(
      width,
      height,
      sheet,
      sheetView,
      Direction.Right,
      Direction.Bottom,
      {
        cellPosition: activeCell,
        fitToCenter: true,
      }
    );

    yield put(
      updateSheetView({
        sheetId,
        ...sheetView,
        ...allViewport,
      })
    );
  }
}
// #endregion

// #region load sheet
export function* loadSheetMi({ payload }: ReturnType<typeof loadSheet>) {
  const { sheetId, windowId } = payload;
  const set: SheetManagementSet = yield generateSheetsDataSet();
  const sheet: Sheet | undefined = yield startReadSheet(
    set,
    sheetId
  );
  const windows: AppWindow[] = yield select(
    (state: RootState) => state.windows.windowList
  );

  console.log('sheet', sheet);

  if (sheet) {
    const window = findWindow(windows, windowId);
    if (window)
      yield call(calculateSheetViewportMi, {
        type: calculateSheetViewport.toString(),
        payload: {
          sheetId,
          width: window.width,
          height: window.height,
          sheet: sheet,
        },
      });
    yield put(addSheet(sheet));
  }
  // const sheet: SnapshotResult<Sheet> = yield startReadSheet(sheetId, true);
  // const windows: AppWindow[] = yield select(
  //   (state: RootState) => state.windows.windowList
  // );
  // if (sheet.data && !sheet.dataLost) {
  //   const window = findWindow(windows, windowId);
  //   console.log('sheet loaded', sheet);
  //   if (window) {
  //     yield call(calculateSheetViewportMi, {
  //       type: calculateSheetViewport.toString(),
  //       payload: {
  //         sheetId,
  //         width: window.width,
  //         height: window.height,
  //         sheet: sheet.data,
  //       },
  //     });
  //   }
  //   yield put(addSheet(sheet.data));
  // }
}
// #endregion

// #region load group
export function* loadSheetGroupMi({
  payload: groupId,
}: ReturnType<typeof loadSheetGroup>) {
  const set: SheetManagementSet = yield generateSheetsDataSet();
  const group: SheetGroup | undefined =
    yield startReadSheetGroup(set, groupId);

  if (group) {
    yield put(addSheetsGroup(group));
  }
}

// #endregion
